import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToastHandler } from "@/hooks/useToastHandler";
import { authApi, GoogleAuthData } from "@/api/authApi";
import { getDeviceTimezoneOffset } from "@/lib/dateUtils";
import { getInvitationTokenFromUrl, storeInvitationToken, retrieveAndClearInvitationToken } from "@/lib/invitationUtils";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void;
          renderButton: (
            element: HTMLElement,
            options: Record<string, unknown>
          ) => void;
          prompt: () => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}

const GoogleLoginButton = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [googleApiLoaded, setGoogleApiLoaded] = useState<boolean>(false);
  const navigate = useNavigate();
  const { handleError, handleSuccess } = useToastHandler();

  useEffect(() => {
    // Check for invitation token in URL and store it
    const invitationToken = getInvitationTokenFromUrl();
    if (invitationToken) {
      storeInvitationToken(invitationToken);
    }

    // Load the Google Sign-In API script
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = initializeGoogleAuth;
    document.body.appendChild(script);

    return () => {
      // Only remove the script if it still exists in the document
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const initializeGoogleAuth = () => {
    if (!window.google) return;

    try {
      // Initialize One Tap or standard sign-in
      window.google.accounts.id.initialize({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
        callback: handleGoogleCallback,
        auto_select: false,
        cancel_on_tap_outside: true,
        //ux_mode: "redirect",
      });

      // Render the Google Sign-In button
      const buttonDiv = document.getElementById("google-signin-button");
      if (buttonDiv) {
        // https://developers.google.com/identity/gsi/web/reference/js-reference
        window.google.accounts.id.renderButton(buttonDiv, {
          theme: "outline",
          size: "large",
          type: "standard",
          text: "signin_with",
          shape: "circular",
          logo_alignment: "left",
          width: 240,
        });
        setGoogleApiLoaded(true);
      }
    } catch (error) {
      console.error("...>>> Failed to initialize Google Auth:", error);
      setGoogleApiLoaded(false);
    }
  };

  const handleGoogleCallback = async (response: { credential?: string }) => {
    setIsLoading(true);
    try {
      const idToken = response.credential;
      if (!idToken) {
        throw new Error("Google authentication failed - no token received");
      }

      // Retrieve stored invitation token if any
      const invitationToken = retrieveAndClearInvitationToken();

      const googleAuthData: GoogleAuthData = {
        idToken,
        deviceTimezoneOffset: getDeviceTimezoneOffset() || "-08:00",
        ...(invitationToken && { invitation_token: invitationToken }),
      };

      await authApi.googleLogin(googleAuthData);
      handleSuccess("Successfully logged in", 1000);
      navigate("/track");
    } catch (error) {
      console.error("...>>> Google login error:", error);
      handleError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle manual login click
  const handleLoginClick = () => {
    if (!window.google) return;

    setIsLoading(true);

    try {
      // Use the One Tap approach directly
      window.google.accounts.id.prompt();
    } catch (error) {
      console.error("...>>> Failed to initiate Google login:", error);
      setIsLoading(false);
      handleError(error as Error);
    }
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <div id="google-signin-button"></div>

      {/* Fallback button if Google API fails to load */}
      {!googleApiLoaded && (
        <button
          onClick={handleLoginClick}
          disabled={isLoading}
          className={`flex items-center justify-center gap-2 w-full py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
            isLoading ? "opacity-70 cursor-not-allowed" : "cursor-pointer"
          }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="20"
            height="20"
          >
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>

          <span>{isLoading ? "Logging in..." : "Sign in with Google"}</span>
        </button>
      )}

      {/* Message when API isn't loaded */}
      {!googleApiLoaded && import.meta.env.DEV && (
        <p className="text-xs text-gray-500 mt-1">
          Note: Make sure VITE_GOOGLE_CLIENT_ID is set in your .env file
        </p>
      )}
    </div>
  );
};

export default GoogleLoginButton;
